import React, { useState } from "react";
import ConsultationForm from "./Consultation"; // adjust path if your pages folder is elsewhere
import Pharmacy1 from "../assets/images/Pharmacy/Pharmacy1.jpg";
import Pharmacy2 from "../assets/images/Pharmacy/Pharmacy2.jpg";
import Pharmacy3 from "../assets/images/Pharmacy/Pharmacy3.jpg";
import Pharmacy4 from "../assets/images/Pharmacy/Pharmacy4.jpg";
import Pharmacy5 from "../assets/images/Pharmacy/Pharmacy5.jpg";
import Pharmacy6 from "../assets/images/Pharmacy/Pharmacy6.jpg";
import Pharmacy7 from "../assets/images/Pharmacy/Pharmacy7.jpg";
import Pharmacy8 from "../assets/images/Pharmacy/Pharmacy8.jpg";
import Pharmacy9 from "../assets/images/Pharmacy/Pharmacy9.jpeg";
import SEO from "../components/SEO";

/**
 * Pharmacy Designs — landing page
 * Same design system as OTSuiteDesigns.jsx / NursingStationDesigns.jsx —
 * black/white/grey/ash palette, spec-line cards (Zone / Size / Coverage),
 * "Get Free Quote" opens the shared Consultation modal.
 */

const FONT_IMPORT_URL =
  "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap";

const tokens = {
  ink: "#343F86",
  bg: "#ECEEF6",
  card: "#FFFFFF",
  teal: "#3A4060",
  tealDark: "#262E63",
  amber: "#8A8F9C",
  blue: "#5B6072",
  hairline: "#CDD1E0",
  sub: "#5B6072",
};

const pharmacyDesigns = [
  {
    name: "Outpatient Pharmacy Counter",
    zone: "Ground Floor",
    size: "22 sq.m",
    spec: "Serves 200+ Patients/Day",
    tag: "Outpatient",
    note: "Multi-window dispensing counter, queue-display integration, private counselling nook.",
    image: Pharmacy1,
  },
  {
    name: "Inpatient Pharmacy Dispensing Station",
    zone: "Level 1",
    size: "26 sq.m",
    spec: "Covers 8 Wards",
    tag: "Inpatient",
    note: "Unit-dose packaging line, ward-wise bin sorting, barcode verification bench.",
    image: Pharmacy2,
  },
  {
    name: "IV Admixture & Compounding Room",
    zone: "Level 2",
    size: "18 sq.m",
    spec: "Cleanroom Grade C",
    tag: "IV Compounding",
    note: "Laminar airflow hoods, gowning anteroom, sterile compounding documentation desk.",
    image: Pharmacy3,
  },
  {
    name: "Oncology Pharmacy Cleanroom",
    zone: "Level 2",
    size: "20 sq.m",
    spec: "Negative-Pressure Isolation",
    tag: "Oncology",
    note: "Biosafety cabinet for cytotoxic prep, closed-system transfer devices, spill-kit station.",
    image: Pharmacy4,
  },
  {
    name: "Emergency Pharmacy Satellite",
    zone: "Ground Floor",
    size: "14 sq.m",
    spec: "24×7 Rapid Access",
    tag: "Emergency",
    note: "Direct ED corridor access, STAT-order pass-through window, code-cart restock counter.",
    image: Pharmacy5,
  },
  {
    name: "Narcotics Vault & Controlled Substance Station",
    zone: "Level 1",
    size: "10 sq.m",
    spec: "Schedule II–V Storage",
    tag: "Controlled Substances",
    note: "Dual-lock vault, biometric access log, dedicated narcotics reconciliation counter.",
    image: Pharmacy6,
  },
  {
    name: "Automated Dispensing Cabinet Hub",
    zone: "Multi-Floor",
    size: "12 sq.m",
    spec: "Covers 6 Nursing Units",
    tag: "Automation",
    note: "Networked ADC refill station, discrepancy-resolution desk, restock cart staging bay.",
    image: Pharmacy7,
  },
  {
    name: "Clinical Pharmacy Consultation Desk",
    zone: "Level 1",
    size: "9 sq.m",
    spec: "1-on-1 Counselling",
    tag: "Clinical Services",
    note: "Private patient-counselling booth, medication-reconciliation terminal, discharge planning corner.",
    image: Pharmacy8,
  },
  {
    name: "Central Pharmacy Warehouse & Bulk Storage",
    zone: "Basement",
    size: "40 sq.m",
    spec: "Hospital-Wide Supply",
    tag: "Central Storage",
    note: "Cold-chain storage units, high-density shelving, bulk receiving and inventory-audit counter.",
    image: Pharmacy9,
  },
];

function SpecLine({ zone, size, spec }) {
  return (
    <div
      style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 11.5,
        letterSpacing: 0.3,
        color: tokens.sub,
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <span>ZONE&nbsp;<b style={{ color: tokens.teal }}>{zone}</b></span>
      <span>·</span>
      <span>SIZE&nbsp;<b style={{ color: tokens.ink }}>{size}</b></span>
      <span>·</span>
      <span>{spec}</span>
    </div>
  );
}

function PharmacyCard({ item, onGetQuote }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        background: tokens.card,
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${tokens.hairline}`,
        boxShadow: hover
          ? "0 14px 28px -14px rgba(14,124,123,0.35)"
          : "0 2px 6px -2px rgba(43,58,66,0.08)",
        transform: hover ? "translateY(-3px)" : "none",
        transition: "all 0.25s ease",
      }}
    >
      {/* corner bracket signature */}
      <div
        style={{
          position: "absolute", top: 10, left: 10, width: 22, height: 22,
          borderTop: `2px solid ${tokens.teal}`, borderLeft: `2px solid ${tokens.teal}`,
          zIndex: 2,
        }}
      />
      <div style={{ height: 168, position: "relative", background: tokens.tealDark }}>
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div
            style={{
              width: "100%", height: "100%", display: "flex",
              alignItems: "center", justifyContent: "center",
              color: "rgba(255,255,255,0.55)",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, letterSpacing: 0.5,
            }}
          >
            ADD IMAGE
          </div>
        )}
        {/* tag chip */}
        <span
          style={{
            position: "absolute", bottom: 10, right: 12,
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
            color: "#fff", background: "rgba(0,0,0,0.45)",
            padding: "2px 8px", borderRadius: 2,
          }}
        >
          {item.tag.toUpperCase()}
        </span>
      </div>
      <div style={{ padding: "16px 16px 18px" }}>
        <h3
          style={{
            fontFamily: "'Montserrat', 'Inter', sans-serif", fontWeight: 600, fontSize: 17.5,
            color: tokens.ink, margin: "0 0 6px", lineHeight: 1.25,
          }}
        >
          {item.name}
        </h3>
        <p style={{ fontSize: 13, color: tokens.sub, margin: "0 0 10px", lineHeight: 1.45 }}>
          {item.note}
        </p>
        <SpecLine zone={item.zone} size={item.size} spec={item.spec} />
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button
            onClick={() => onGetQuote(item)}
            style={{
              flex: 1, padding: "9px 10px", fontSize: 12.5, fontWeight: 600,
              fontFamily: "'Montserrat', 'Inter', sans-serif", letterSpacing: 0.2,
              background: tokens.amber, color: tokens.ink,
              border: "none", borderRadius: 3, cursor: "pointer",
            }}
          >
            Get Free Quote
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PharmacyDesigns() {
  const [showConsultation, setShowConsultation] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleGetQuote = (item) => {
    setSelectedItem(item);
    setShowConsultation(true);
  };

  return (
     <>
    <SEO/>
    <div
      style={{
        background: tokens.bg,
        color: tokens.ink,
        fontFamily: "'Inter', 'Montserrat', sans-serif",
        minHeight: "100vh",
      }}
    >
      <style>{`@import url('${FONT_IMPORT_URL}');
        * { box-sizing: border-box; }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
        button:focus-visible, a:focus-visible { outline: 2px solid ${tokens.teal}; outline-offset: 2px; }
        .pharmacy-grid-3x3 { grid-template-columns: repeat(3, 1fr); }
        @media (max-width: 900px) { .pharmacy-grid-3x3 { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 600px) { .pharmacy-grid-3x3 { grid-template-columns: 1fr; } }
        .pharmacy-modal-overlay {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(0, 0, 0, 0.65);
          backdrop-filter: blur(5px);
          display: flex; align-items: center; justify-content: center;
          padding: 1rem;
        }
        .pharmacy-modal-inner {
          position: relative; width: 100%; max-width: 680px;
          max-height: 90vh; overflow-y: auto; border-radius: 18px;
        }
        .pharmacy-modal-close {
          position: absolute; top: 14px; right: 16px; z-index: 10;
          background: #262E63; border: none; color: #fff;
          width: 32px; height: 32px; border-radius: 50%;
          font-size: 20px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          line-height: 1;
        }
      `}</style>

      {/* spacer so the fixed/sticky page navbar in the host app doesn't overlap the grid */}
      <div style={{ height: 32 }} />

      {/* GRID */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "26px 24px 10px" }}>
        <div
          className="pharmacy-grid-3x3"
          style={{
            display: "grid",
            gap: 22,
            marginTop: 70,
          }}
        >
          {pharmacyDesigns.map((item, i) => (
            <PharmacyCard key={i} item={item} onGetQuote={handleGetQuote} />
          ))}
        </div>
      </section>

      {/* Consultation Modal — opens on "Get Free Quote" click */}
      {showConsultation && (
        <div
          className="pharmacy-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowConsultation(false); }}
        >
          <div className="pharmacy-modal-inner">
            <button
              className="pharmacy-modal-close"
              onClick={() => setShowConsultation(false)}
              aria-label="Close consultation form"
            >
              ×
            </button>
            <ConsultationForm suite={selectedItem} />
          </div>
        </div>
      )}
    </div>
    </>
  );
}